import { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { validarRangoFechas } from '../../../helpers/dateValidation.js'
import {
  setActivo,
  setExposicion as setExposicionFiltros,
  setListaVenta,
  setListaEnvioVencidaSeleccionada
} from '../../../slices/filterSlice'
import Publicacion from '../../publicaciones/Publicacion'
import { Filtros } from '../../reutilizables/filtros/Filtros'
import { OrdenarPor } from '../../reutilizables/ordenamiento/OrdenarPor'
import { VistaCuadricula } from '../../reutilizables/ordenamiento/VistaCuadricula'
import { Paginacion } from '../../reutilizables/paginacion/Paginacion'
import { buscarListaPorVendedor as buscarListaEnvioPorVendedor } from '../../servicios/listaEnvioService'
import { buscarListaPorVendedor } from '../../servicios/listaVentaService'
import { buscarExposicion, buscarTodasOptimizado } from '../../servicios/publicacionesService'
import ComponenteError from '../ComponenteError'
import styles from '../Publicaciones.module.css'

const ITEMS_PAGINA = 15
const ENVIO_A_CARGO_COMPRADOR = 3
const ESTADO_PUBLICADA = 2

const Publicaciones = () => {
  const idVendedor = parseInt(localStorage.getItem('idUsuario'))
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [publicaciones, setPublicaciones] = useState([])
  const [error, setError] = useState(false)
  const [exposiciones, setExposiciones] = useState([])
  const [listasVentaConDatos, setListasVentaConDatos] = useState([])
  const [listasEnviosVencidas, setListasEnviosVencidas] = useState([])
  const [itemsPorPagina, setItemsPorPagina] = useState(ITEMS_PAGINA)
  const [paginaActual, setPaginaActual] = useState(1)
  const topRef = useRef(null)
  const [textoBusqueda, setTextoBusqueda] = useState('')
  const [vistaCuadricula, setVistaCuadricula] = useState(false)

  const scrollToTop = () => {
    if (typeof topRef.current?.scrollIntoView === 'function') {
      topRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const {
    exposicion: exposicionFiltro,
    listaVenta,
    publicacionesFiltradas,
    activo,
    listaEnvioVencidaSeleccionada
  } = useSelector((state) => state.filters)

  const filtrarPorListaVencida = (listas) => {
    return listas.reduce((acc, lista) => {
      const estaVigente = validarRangoFechas(new Date(), lista.vigenciaHasta, 'day')
      if (!estaVigente) {
        acc.push({
          key: lista.idLista,
          value: lista.nombreLista.trim().toLowerCase(),
          label: lista.nombreLista,
          vigenciaHasta: lista.vigenciaHasta
        })
      }
      return acc
    }, [])
  }

  const fetchData = async () => {
    const { data } = await buscarTodasOptimizado(0, idVendedor, 0, '')
    const listasVentas = await buscarListaPorVendedor(idVendedor)
    const listasEnvios = await buscarListaEnvioPorVendedor(idVendedor)

    const listaEnviosUnificadas = Object.values(
      listasEnvios.data.reduce((acc, item) => {
        const key = item.nombreLista.trim().toLowerCase()
        if (!acc[key]) {
          acc[key] = {
            idLista: item.idLista,
            nombreLista: item.nombreLista,
            vigenciaDesde: item.vigenciaDesde,
            vigenciaHasta: item.vigenciaHasta,
            idVendedor: item.idVendedor,
            grilla: []
          }
        }

        if (Array.isArray(item.grilla)) {
          acc[key].grilla.push(...item.grilla)
        }

        return acc
      }, {})
    )

    const nombreListasEnUso = new Set(
      data.flatMap((pub) =>
        (pub.mediosEnvios ?? [])
          .filter(
            (m) =>
              m.idMedioEnvio === ENVIO_A_CARGO_COMPRADOR &&
              m.nombreLista &&
              pub.idPublicacionEstado === ESTADO_PUBLICADA
          )
          .map((m) => m.nombreLista.trim().toLowerCase())
      )
    )

    const listaEnviosEnUso = listaEnviosUnificadas.filter((l) =>
      nombreListasEnUso.has(l.nombreLista.trim().toLowerCase())
    )
    const listasEnvioVencidas = filtrarPorListaVencida(listaEnviosEnUso)
    setListasEnviosVencidas(listasEnvioVencidas)

    const listaIds = new Set(data.map((p) => p.idListaVenta))
    const listasVentasConDatos = listasVentas.data.filter((l) => listaIds.has(l.idListaVenta))
    setListasVentaConDatos(listasVentasConDatos)

    const exposicion = await buscarExposicion('')
    setExposiciones(exposicion.data)

    const publicaciones = data.map((pub) => {
      const listaMatch = listasVentas.data.find((l) => l.idListaVenta === pub.idListaVenta)
      const porcDctoPublicacion = Number(pub.porcDctoPublicacion ?? 0)
      const porcDctoLista = Number(pub.porcDctoLista ?? 0)
      let porcDctoMostrar = 0
      let origenDescuento = 'ninguno'
      if (porcDctoPublicacion > 0) {
        porcDctoMostrar = porcDctoPublicacion
        origenDescuento = 'publicacion'
      } else if (porcDctoLista > 0) {
        porcDctoMostrar = porcDctoLista
        origenDescuento = 'lista'
      }

      return {
        ...pub,
        porcDctoMostrar,
        origenDescuento,
        publicacionExposicion:
          exposicion.data.find((e) => e.idPublicacionExposicion === pub.idPublicacionExposicion)
            ?.nombre || '',
        listaVenta: listaMatch?.nombre || ''
      }
    })
    setPublicaciones(publicaciones)
    setError(false)
  }

  useEffect(() => {
    if (!isNaN(idVendedor) && idVendedor !== null && idVendedor !== undefined) {
      fetchData()
    }
  }, [idVendedor])

  useEffect(() => {
    setPaginaActual(1)
    scrollToTop()
  }, [publicaciones])

  const handleToggle = (event, publicacion, idPublicacion) => {
    navigate(`/Publicaciones/Administrar/${idPublicacion}`)
    window.scrollTo(0, 0)
  }

  const filtrosFijos = [
    {
      titulo: 'Tipo de Exposición',
      value: exposicionFiltro,
      opciones: exposiciones.map((e) => ({
        label: e.nombre,
        valor: e.idPublicacionExposicion
      })),
      onChange: (v) => dispatch(setExposicionFiltros(v))
    },
    {
      titulo: 'Listas Ventas',
      value: listaVenta,
      opciones: listasVentaConDatos.map((l) => ({ label: l.nombre, valor: l.idListaVenta })),
      onChange: (v) => dispatch(setListaVenta(v))
    },
    {
      titulo: 'Activo',
      value: activo,
      opciones: [...new Set(publicaciones.map((pub) => pub.publicacionEstado))].map((estado) => ({
        label: estado,
        valor: estado
      })),
      onChange: (v) => dispatch(setActivo(v))
    },
    {
      titulo: 'Lista Envio Vencida',
      value: listaEnvioVencidaSeleccionada,
      opciones: listasEnviosVencidas.map((l) => ({
        label: l.label,
        valor: l.value
      })),
      onChange: (v) => dispatch(setListaEnvioVencidaSeleccionada(v))
    }
  ]

  const totalPaginas = Math.max(1, Math.ceil(publicacionesFiltradas.length / itemsPorPagina))
  const indiceUltimoItem = paginaActual * itemsPorPagina
  const indicePrimerItem = indiceUltimoItem - itemsPorPagina
  const publicacionesMostradas = publicacionesFiltradas.slice(indicePrimerItem, indiceUltimoItem)

  return (
    <div className={styles.layout} ref={topRef}>
      {publicaciones.length > 0 && (
        <aside className={styles.filtros}>
          <Filtros
            publicaciones={publicaciones}
            filtrosFijos={filtrosFijos}
            mostrarFiltroPrecio={false}
            buscador={{
              onChange: () => {},
              placeholder: 'Buscar por titulo o id...',
              valor: textoBusqueda,
              setBusqueda: setTextoBusqueda
            }}
            mostrarLimpiarFiltros={
              listaVenta ||
              exposicionFiltro ||
              textoBusqueda ||
              activo ||
              listaEnvioVencidaSeleccionada
            }
          />
        </aside>
      )}
      <fieldset className={`fieldset ${styles.fieldset}`}>
        <div className={styles.headerPublicaciones}>
          <legend>Administrar Publicaciones</legend>
          <div className={styles.accionesVista}>
            <OrdenarPor
              options={[
                {
                  label: 'Más recientes',
                  value: { orden: 'DESC', campo: 'idPublicacion' }
                },
                {
                  label: 'Más antiguos',
                  value: { orden: 'ASC', campo: 'idPublicacion' }
                }
              ]}
            />
            <VistaCuadricula
              requiereCuadricula={true}
              vistaCuadricula={vistaCuadricula}
              setVistaCuadricula={setVistaCuadricula}
            />
          </div>
        </div>
        {publicacionesMostradas.length !== 0 && (
          <>
            {error ? (
              <ComponenteError tipo="I" idAviso={11} key={1} />
            ) : (
              <>
                <div className={vistaCuadricula ? styles.gridContainer : ''}>
                  {publicacionesMostradas.map((publicacion) => (
                    <div
                      key={publicacion.idPublicacion}
                      className={vistaCuadricula ? '' : styles.publicacion}
                    >
                      <Publicacion
                        vieneDeAdministrar={true}
                        onToggleClick={handleToggle}
                        publicacion={publicacion}
                        vistaCuadricula={vistaCuadricula}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {publicacionesMostradas.length === 0 && <ComponenteError tipo="I" idAviso={8} key={2} />}
        <Paginacion
          totalPaginas={totalPaginas}
          paginaActual={paginaActual}
          onCambioPagina={(num) => {
            setPaginaActual(num)
            scrollToTop()
          }}
          itemsPorPagina={itemsPorPagina}
          onCambioItemsPorPagina={(num) => {
            setItemsPorPagina(num)
            setPaginaActual(1)
            scrollToTop()
          }}
        />
      </fieldset>
    </div>
  )
}

export default Publicaciones
